import { Box, Stack, Typography, useTheme } from '@mui/material';
import {
  GridChartsIntegrationContextProvider,
  GridChartsRendererProxy,
  isAutogeneratedRow,
  type GridColumnVisibilityModel,
  type GridColDef,
  type GridChartsPanelProps,
  type GridRowParams,
} from '@mui/x-data-grid-premium';
import {
  ChartsRenderer,
  configurationOptions,
  type ChartsRendererProps,
} from '@mui/x-charts-premium/ChartsRenderer';
import { useCallback, useMemo, useState } from 'react';
import type { CustomField } from '../GeneratedClient';
import { v2DataGridStyles } from '../Families/v2DataGridStyles';
import type {
  ReferralAssignmentRoleV2,
  ReferralBrowserRowV2,
} from './referralBrowserTypes';
import {
  buildReferralsColumns,
  isInternalReferralsColumn,
  isOptionalReferralsColumn,
  REFERRALS_SEARCH_FIELD,
} from './referralsGridColumns';
import { referralsInitialSortModel } from './referralsGridSorting';
import { ReferralsChartsPanel } from './ReferralsChartsPanel';
import { usePersistedGridFilterModel } from '../Hooks/usePersistedGridFilterModel';
import { PersistedFilterDataGridPremium } from '../Generic/PersistedFilterDataGridPremium';

type ReferralsDataGridV2Props = {
  assignmentRoles?: ReferralAssignmentRoleV2[];
  counties: string[];
  customFields: CustomField[];
  expanded?: boolean;
  loading?: boolean;
  onRowClick: (row: ReferralBrowserRowV2) => void;
  rows: ReferralBrowserRowV2[];
};

const referralsGridSlots = { noRowsOverlay: ReferralsEmptyState };
const referralsGridSlotProps = {
  chartsPanel: {
    schema: {
      bar: configurationOptions.bar,
      column: configurationOptions.column,
      pie: configurationOptions.pie,
    },
  },
  toolbar: {
    quickFilterProps: {
      quickFilterFormatter: (values: unknown[]) => values.join(' '),
      quickFilterParser: (searchText: string) =>
        searchText.trim() === '' ? [] : [searchText.trim()],
    },
  },
  columnsManagement: {
    getTogglableColumns: (columns: GridColDef[]) =>
      columns
        .filter((column) => !isInternalReferralsColumn(column.field))
        .map((column) => column.field),
  },
};

function getReferralsRowHeight() {
  return 'auto' as const;
}

function getEstimatedReferralsRowHeight() {
  return 72;
}

function ReferralsEmptyState() {
  return (
    <Stack
      spacing={0.5}
      sx={{
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 180,
        px: 2,
        textAlign: 'center',
      }}
    >
      <Typography className="ph-unmask" variant="subtitle1">
        No referrals found.
      </Typography>
      <Typography className="ph-unmask" color="text.secondary" variant="body2">
        Referrals will appear here when they are available.
      </Typography>
    </Stack>
  );
}

export function ReferralsDataGridV2({
  assignmentRoles = [],
  counties,
  customFields,
  expanded = false,
  loading = false,
  onRowClick,
  rows,
}: ReferralsDataGridV2Props) {
  const columns = useMemo(
    () =>
      buildReferralsColumns(
        rows,
        assignmentRoles,
        customFields,
        counties,
        expanded
      ),
    [assignmentRoles, counties, customFields, expanded, rows]
  );
  const sessionKey = JSON.stringify([
    rows.length === 0,
    customFields.map(({ name, type, validValues }) => [
      name,
      type,
      validValues,
    ]),
    columns.map(({ field, type, pivotable, chartable }) => [
      field,
      type,
      pivotable,
      chartable,
    ]),
  ]);

  return (
    <ReferralsGridSession
      key={sessionKey}
      columns={columns}
      rows={rows}
      loading={loading}
      onRowClick={onRowClick}
    />
  );
}

function ReferralsGridSession({
  columns,
  rows,
  loading,
  onRowClick,
}: Pick<ReferralsDataGridV2Props, 'rows' | 'loading' | 'onRowClick'> & {
  columns: GridColDef<ReferralBrowserRowV2>[];
}) {
  const theme = useTheme();
  const [pivotActive, setPivotActive] = useState(false);
  const { filterModel, filterModelRestored, onFilterModelChange } =
    usePersistedGridFilterModel({
      columns,
      namespace: 'referrals',
    });
  const slots = useMemo(
    () => ({
      ...referralsGridSlots,
      chartsPanel: (props: GridChartsPanelProps) => (
        <ReferralsChartsPanel {...props} pivotActive={pivotActive} />
      ),
    }),
    [pivotActive]
  );
  const renderChart = useCallback(
    (props: ChartsRendererProps) =>
      pivotActive && props.chartType ? (
        <Box sx={{ width: '100%', minWidth: 0, overflow: 'auto' }}>
          <ChartsRenderer {...props} />
        </Box>
      ) : null,
    [pivotActive]
  );
  const [sortModel, setSortModel] = useState(referralsInitialSortModel);
  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({});
  const visibleColumns = useMemo(
    () => ({
      ...Object.fromEntries(
        columns
          .filter((column) => isOptionalReferralsColumn(column.field))
          .map((column) => [column.field, false])
      ),
      ...columnVisibilityModel,
      [REFERRALS_SEARCH_FIELD]: false,
    }),
    [columnVisibilityModel, columns]
  );
  const handleRowClick = useCallback(
    ({ row }: GridRowParams<ReferralBrowserRowV2>) => {
      if (!pivotActive && !isAutogeneratedRow(row) && row.id) onRowClick(row);
    },
    [onRowClick, pivotActive]
  );

  return (
    <GridChartsIntegrationContextProvider>
      <Box
        sx={{
          height: '100%',
          width: '100%',
          minHeight: 0,
          minWidth: 0,
          overflowY: 'auto',
        }}
      >
        <Box
          sx={[
            v2DataGridStyles(theme, { height: '100%' }),
            { minHeight: 'min(360px, 100%)', minWidth: 0 },
          ]}
        >
          <PersistedFilterDataGridPremium
            showToolbar
            chartsIntegration
            disableAggregation
            disableRowGrouping
            pivotActive={pivotActive}
            onPivotActiveChange={setPivotActive}
            aggregationRowsScope="filtered"
            pagination={false}
            rows={rows}
            columns={columns}
            sortModel={pivotActive ? undefined : sortModel}
            onSortModelChange={pivotActive ? undefined : setSortModel}
            filterModel={filterModel}
            filterModelRestored={filterModelRestored}
            onFilterModelChange={onFilterModelChange}
            filterDebounceMs={200}
            columnVisibilityModel={visibleColumns}
            onColumnVisibilityModelChange={
              pivotActive ? undefined : setColumnVisibilityModel
            }
            columnHeaderHeight={42}
            density="comfortable"
            disableRowSelectionOnClick
            getRowHeight={getReferralsRowHeight}
            getEstimatedRowHeight={getEstimatedReferralsRowHeight}
            loading={loading}
            onRowClick={handleRowClick}
            slots={slots}
            slotProps={referralsGridSlotProps}
          />
        </Box>
        <GridChartsRendererProxy
          id="referrals-report"
          label="Referral counts"
          renderer={renderChart}
        />
      </Box>
    </GridChartsIntegrationContextProvider>
  );
}
